import { type DataSourceColumn, type SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import { isUndefined } from 'lodash';
import type jsPDF from 'jspdf';
import { type CellHookData } from 'jspdf-autotable';
import { type TableColumn } from '../../types';
import { type Hierarchy } from 'common/visualizations/vif';
import { type Row } from '../../processors/type';
import { isHeaderSection } from './tableStyleUtils';
import { type TableColumnFormat } from 'common/visualizations/views/agGridReact/types';
import { type ViewColumn } from 'common/tests/dataMimics/View/types';

export const setDisplayName = (reportFilters: SoqlFilter[] | undefined, tableColumns: TableColumn[]) => {
  if (!isUndefined(reportFilters)) {
    reportFilters.forEach((vifFilter: SoqlFilter) => {
      const newColumnsWithDisplayName = vifFilter.columns.map((column: DataSourceColumn) => {
        const matchedCol = tableColumns.find((tableCol) => tableCol.field === column.fieldName);
        column.displayName = matchedCol?.displayName;
        return column;
      });
      vifFilter.columns = newColumnsWithDisplayName;
    });
  }
};

export const getFiltersWithDisplayNames = (
  reportFilters: SoqlFilter[] | undefined,
  columnFormats: { [key: string]: TableColumnFormat },
  viewColumns: ViewColumn[] | undefined,
): SoqlFilter[] | undefined => {
  if (!reportFilters) {
    return reportFilters;
  }

  return reportFilters.map((vifFilter: SoqlFilter) => ({
    ...vifFilter,
    columns: vifFilter.columns.map((column: DataSourceColumn) => {
      // Priority: columnFormats > viewColumns > existing displayName > fieldName
      let resolvedDisplayName: string | undefined = columnFormats[column.fieldName]?.displayName;

      if (!resolvedDisplayName && viewColumns) {
        const matchedViewCol = viewColumns.find((viewCol) => viewCol.fieldName === column.fieldName);
        resolvedDisplayName = matchedViewCol?.name;
      }

      return {
        ...column,
        displayName: resolvedDisplayName || column.displayName || column.fieldName,
      };
    }),
  }));
};

export const getTotalLabel = (
  isAdvancedTotalingForExportsEnabled: boolean | undefined,
  hierarchyConfig: Hierarchy | undefined,
) => {
  const totalLabel = isAdvancedTotalingForExportsEnabled
    ? hierarchyConfig?.customLabels?.totals || 'Total'
    : 'Total';

  return totalLabel;
};

const DEFAULT_BORDER_COLOR = '#000000';

// re-asserts the table border color at the start of each page. autotable only re-applies
// lineColor when a cell carries one, so on a fresh page the stroke otherwise inherits the
// reset graphics-state default and borders fade in Safari/Firefox.
// borderColor comes from getPropertyValue('--forge-theme-border-color'), which returns '' when
// the var is unresolved, and jspdf treats setDrawColor('') as a no-op. fall back to black so the
// page still gets a valid stroke instead of inheriting the previous page's faded color.
export const applyPageBorderColor = (doc: jsPDF, borderColor: string) => {
  doc.setDrawColor(borderColor || DEFAULT_BORDER_COLOR);
};

const DIV_ZERO_TEXT_COLOR: [number, number, number] = [255, 0, 0];

// paints the error text red in the pdf when the row flagged this field as a safe-division sentinel.
// scoped to body cells so the column header above a flagged value isn't repainted - autotable
// reuses the same row index across head/body sections, which would otherwise leak the style up.
export const applyDivZeroStyling = (data: CellHookData, row?: Row, fieldName?: string) => {
  if (isHeaderSection(data.section)) {
    return;
  }
  if (fieldName && row?._divZeroFields?.includes(fieldName)) {
    data.cell.styles.textColor = DIV_ZERO_TEXT_COLOR;
  }
};
